"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Mail, ShieldCheck, Newspaper, Clock, Send, Loader, CircleCheck as CheckCircle2, CircleAlert as AlertCircle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
interface EditorialChannel {
  title: string;
  description: string;
  email: string;
  responseWindow: string;
}
const editorialChannels: EditorialChannel[] = [{
  title: "Editorial Corrections & Evidence Review",
  description: "Submit citations, updated veterinary studies, or factual errata directly to our medical desk.",
  email: "corrections@petswhirl.com",
  responseWindow: "Reviewed within 24 hours"
}, {
  title: "Press & Media Inquiries",
  description: "Contact our editorial leadership for commentary on veterinary findings and pet safety data.",
  email: "press@petswhirl.com",
  responseWindow: "Same business day"
}, {
  title: "Veterinary Review Board",
  description: "Questions regarding our clinical review protocol or applications for accredited veterinary contributors.",
  email: "vet-board@petswhirl.com",
  responseWindow: "2 to 3 business days"
}];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-card p-8 text-center text-card-foreground shadow-xs">
        <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-muted text-primary">
          <CheckCircle2 className="size-6" />
        </div>
        <h3 className="font-serif text-2xl font-semibold tracking-tight text-foreground">
          Inquiry received
        </h3>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground">
          {successMessage || "Thank you. Our editorial and medical review team will review your message promptly."}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-border font-medium">
          Send another inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="name" rules={{
      required: "Your name is required",
      minLength: {
        value: 2,
        message: "Minimum 2 characters required"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-semibold text-foreground">
              Your Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Jane Doe" className="h-11 rounded-lg border-border bg-background px-4 text-foreground placeholder:text-muted-foreground focus-visible:ring-ring" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please provide a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-semibold text-foreground">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="jane@example.com" className="h-11 rounded-lg border-border bg-background px-4 text-foreground placeholder:text-muted-foreground focus-visible:ring-ring" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="subject" rules={{
      required: "Please select an inquiry type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-semibold text-foreground">
              Subject
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <SelectTrigger className="h-11 w-full rounded-lg border-border bg-background px-4 text-left text-foreground placeholder:text-muted-foreground focus-visible:ring-ring">
                  <SelectValue placeholder="Select an inquiry type" />
                </SelectTrigger>
                <SelectContent className="border-border bg-popover text-popover-foreground">
                  <SelectItem value="General Inquiry">General Inquiry</SelectItem>
                  <SelectItem value="Editorial Correction">Editorial Correction</SelectItem>
                  <SelectItem value="Vet Review Question">Vet Review Question</SelectItem>
                  <SelectItem value="Partnership">Partnership</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Please enter at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-sm font-semibold text-foreground">
              Message
            </FormLabel>
            <FormControl>
              <Textarea placeholder="How can our editorial team help you?" rows={5} className="min-h-32 rounded-lg border-border bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground focus-visible:ring-ring" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="flex items-start gap-3 rounded-lg border border-destructive/20 bg-destructive/5 p-4 text-destructive">
          <AlertCircle className="size-5 shrink-0" />
          <p className="text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="h-12 w-full rounded-lg bg-primary text-base font-semibold text-primary-foreground shadow-sm transition-[background-color,transform] duration-200 hover:bg-primary/90 focus-visible:ring-ring">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Sending message...
          </> : <>
            <Send className="mr-2 size-4" />
            Submit Inquiry
          </>}
      </Button>
    </Form>;
}
export default function Contact() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="contact" data-nav="light" className="bg-background py-16 md:py-24 text-foreground">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-muted px-3.5 py-1 text-xs font-semibold tracking-wider uppercase text-foreground">
            <ShieldCheck className="size-3.5 text-primary" />
            Editorial Governance
          </div>
          <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Contact our editorial team
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Direct your inquiry to our medical editors, veterinary reviewers, or administrative desks for evidence-informed assistance.
          </p>
        </div>

        {/* Main 2-Column Grid */}
        <div className="mt-12 grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left Column: Guidelines & Contact Details */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.4,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex flex-col justify-between space-y-8 lg:col-span-5">
            <div className="space-y-6">
              {/* Clinical Governance Callout Motif */}
              <div className="rounded-r-lg border-y border-r border-l-4 border-border border-l-primary bg-card p-6 text-card-foreground shadow-2xs">
                <div className="flex items-center gap-2 font-serif text-base font-semibold text-foreground">
                  <Newspaper className="size-4 text-primary" />
                  Editorial Standards & Corrections
                </div>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  PetsWhirl upholds rigorous clinical review standards. If you identify a potential inaccuracy, outdated guideline, or have updated veterinary evidence to contribute, our board assesses every report systematically.
                </p>
              </div>

              {/* Editorial Channels List */}
              <div className="space-y-4">
                <h3 className="font-serif text-lg font-semibold text-foreground">
                  Dedicated Communication Desks
                </h3>

                <div className="space-y-3.5">
                  {editorialChannels.map((channel, i) => <div key={channel.title} data-index={i} className="rounded-xl border border-border bg-card p-5 text-card-foreground transition-[border-color,box-shadow] duration-200 hover:border-border hover:shadow-xs">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="text-sm font-semibold text-foreground">
                          {channel.title}
                        </h4>
                      </div>
                      <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                        {channel.description}
                      </p>
                      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-3 text-xs">
                        <a href={`mailto:${channel.email}`} className="inline-flex items-center gap-1.5 font-medium text-foreground underline-offset-4 hover:underline">
                          <Mail className="size-3.5 text-primary" />
                          {channel.email}
                        </a>
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <Clock className="size-3" />
                          {channel.responseWindow}
                        </span>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>

            {/* Editorial Office Footer Note */}
            <div className="border-t border-border pt-6 text-xs text-muted-foreground">
              <p className="leading-normal">
                PetsWhirl publication offices are open Monday through Friday, 9:00 AM to 6:00 PM EST. Urgent veterinary safety reports are processed on priority rotation.
              </p>
            </div>
          </motion.div>

          {/* Right Column: Contact Form */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.4,
          delay: 0.08,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-7">
            <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 lg:p-10 shadow-xs">
              <div className="mb-6 border-b border-border pb-6">
                <h3 className="font-serif text-2xl font-semibold text-foreground">
                  Editorial Contact Form
                </h3>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  Complete the fields below to transmit your message to our editorial desk.
                </p>
              </div>

              <FormProvider formId="about_petswhirl_contact_form" sectionName="contact-section" defaultValues={{
              name: "",
              email: "",
              subject: "",
              message: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2,
                maxLength: 60
              },
              email: {
                required: true,
                pattern: "email"
              },
              subject: {
                required: true
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="Submit Inquiry" successMessage="Your inquiry has been submitted to the PetsWhirl editorial desk." errorMessage="Unable to send message. Please review the form or email us directly.">
                <ContactFormInner />
              </FormProvider>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}